import {
  createProperty,
  getPropertyByID,
  putPropertyContacts,
  putPropertyRules,
  putPropertyRulesets,
  putPropertyUsers,
  updatePermit,
  updateProperty,
} from 'api';
import { VerticalTabLayout } from 'components/layout/VerticalTabLayout';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { getBlankProperty, logError } from 'utils';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionFunction,
  Outlet,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import {
  EntityTypes,
  FormMode,
  PanelRootContext,
  Property,
  TraxeroActionResponse,
} from 'types';
import AuthService from 'utils/AuthService';

export type PropertyTabs =
  | 'people'
  | 'jobs'
  | 'permits'
  | 'property'
  | 'rule-config'
  | 'permitTypes';

export interface PropertyRouteParams {
  params: {
    propertyID?: string | undefined;
    permitID?: string | undefined;
    tenantID?: string | undefined;
  };
}

export interface PropertyLoaderData {
  _property: Promise<Property>;
}

export enum PropertiesActionHandler {
  CREATE = 'create',
  UPDATE = 'UPDATE',
  UPDATE_CONTACTS = 'updateContacts',
  UPDATE_USERS = 'updateUsers',
  UPDATE_RULES = 'updateRules',
  UPDATE_RULESETS = 'updateRulesets',
  UPDATE_QR_CODES = 'updateQRCodes',
  UPDATE_PERMITS = 'updatePermits',
  UNKNOWN = 'unknown',
}

export const propertyLoader = ({
  params,
}: PropertyRouteParams): PropertyLoaderData => {
  const id = Number(params.propertyID);
  if (!params.propertyID || isNaN(id) || id <= 0) {
    throw new Response('Invalid property ID', { status: 404 });
  }

  return {
    _property: getPropertyByID({
      tenantID: params.tenantID,
      propertyID: params.propertyID,
    }),
  };
};

export const PropertyLayout = (): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const { propertyID } = useParams();
  const { _property } = useLoaderData() as PropertyLoaderData;
  const [isLoading, setIsLoading] = useState(true);
  const { pathname } = useLocation();
  const [formMode, setFormMode] = useState<FormMode>('none');
  const [tab, setTab] = useState<PropertyTabs>('property');
  const [property, setProperty] = useState<Property>(
    getBlankProperty(state.tenant.id),
  );

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: property?.name || '',
    });
  }, [property?.name]);

  // permissions
  const allowedTabs: PropertyTabs[] = useMemo(() => {
    const propertyTabs: PropertyTabs[] = [
      'property',
      'jobs', // check view jobs
      'permits', // check view permits
      'permitTypes',
      'people',
      'rule-config', // check view rules
    ];
    if (AuthService.IsSuperAdmin()) {
      return propertyTabs;
    }
    return propertyTabs.filter((tab) => {
      switch (tab) {
        case 'property':
        case 'people':
          return true;
        case 'jobs':
          return state.entityPermissions.job.read;
        case 'permits':
          return (
            state.entityPermissions.permit.read && state.features.permitService
          );
        case 'rule-config':
          return state.entityPermissions.rule.read;
        case 'permitTypes':
          return (
            state.entityPermissions.permit.read && state.features.permitService
          );
        default:
          return false;
      }
    });
  }, [state.entityPermissions, state.features]);

  // async loading
  useEffect(() => {
    const awaitLoader = async () => {
      try {
        const p = await _property;
        setProperty(p);
        // resolve loader
        setIsLoading(false);

        dispatch({
          type: ACTION_TYPE.SET_HEADER_TEXT,
          payload: `${p.name}`,
        });

        dispatch({
          type: ACTION_TYPE.SET_ENTITY_NAME,
          payload: `${p.name}`,
        });
      } catch (e) {
        logError(e as Error);
        navigate(-1);
      }
    };
    awaitLoader();
  }, [_property]);

  const getSuccessMessage = useCallback(
    (response: TraxeroActionResponse<PropertiesActionHandler>) => {
      switch (response.handler) {
        case PropertiesActionHandler.UPDATE_CONTACTS:
          return t('update_success', {
            name: t('contact.label_plural'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.UPDATE_USERS:
          return t('update_success', {
            name: t('user.label_plural'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.UPDATE_RULES:
          return t('update_success', {
            name: t('rule.label_plural'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.UPDATE_RULESETS:
          return t('update_success', {
            name: t('ruleset.label_plural'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.CREATE:
          return t('create_success', {
            name: t('property.label'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.UPDATE:
          return t('update_success', {
            name: t('property.label'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.UPDATE_PERMITS:
          return t('create_success', { name: t('permit.label'), ns: 'toasts' });
        case PropertiesActionHandler.UPDATE_QR_CODES:
          return t('update_success', {
            name: t('qrCodes.label'),
            ns: 'toasts',
          });
        default:
          return t('update_success', {
            name: t('property.label'),
            ns: 'toasts',
          });
      }
    },
    [t],
  );
  const getErrorMessage = useCallback(
    (response: TraxeroActionResponse<PropertiesActionHandler>) => {
      switch (response.handler) {
        case PropertiesActionHandler.UPDATE_CONTACTS:
          return t('update_error', {
            name: t('contact.label_plural'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.UPDATE_USERS:
          return t('update_error', {
            name: t('user.label_plural'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.UPDATE_RULES:
          return t('update_error', {
            name: t('rule.label_plural'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.UPDATE_RULESETS:
          return t('update_error', {
            name: t('ruleset.label_plural'),
            ns: 'toasts',
          });
        case PropertiesActionHandler.CREATE:
          return t('create_error', { name: t('property.label'), ns: 'toasts' });
        case PropertiesActionHandler.UPDATE:
          return t('update_error', { name: t('property.label'), ns: 'toasts' });
        case PropertiesActionHandler.UPDATE_PERMITS:
          return t('create_error', { name: t('permit.label'), ns: 'toasts' });
        default:
          return t('update_error', { name: t('property.label'), ns: 'toasts' });
      }
    },
    [t],
  );

  // form submission response handler
  const response =
    fetcher.data as TraxeroActionResponse<PropertiesActionHandler>;
  useEffect(() => {
    if (response) {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher]);

  // Effect to update tab based on URL change
  const propertyDetailSubPaths = ['files', 'branding', 'qrCodes', 'donottow'];
  useEffect(() => {
    const splitPath = pathname.split('/');
    let currentTab;
    if (splitPath.length === 5 || propertyDetailSubPaths.includes(splitPath[5])) {
      currentTab = 'property';
    } else {
      currentTab = splitPath[5];
    }
    if (tab !== currentTab) {
      setTab(currentTab as PropertyTabs);
    }
  }, [pathname, tab]);

  const getTabRoute = (tab: PropertyTabs): string => {
    let route: ROUTES;
    switch (tab) {
      case 'people':
        route = ROUTES.PROPERTY_PEOPLE;
        break;
      case 'permits':
        route = ROUTES.PROPERTY_PERMITS;
        break;
      case 'jobs':
        route = ROUTES.PROPERTY_JOBS;
        break;
      case 'rule-config':
        route = ROUTES.PROPERTY_RULES;
        break;
      case 'permitTypes':
        route = ROUTES.PROPERTY_PERMIT_TYPES;
        break;
      case 'property':
        route = ROUTES.PROPERTY;
        break;
      default:
        route = ROUTES.PROPERTY;
        break;
    }
    return routeWithParams(route, [state.tenant.id, Number(propertyID)]);
  };

  const onNavigate = useCallback(() => {
    setFormMode('none');
  }, []);

  return (
    <VerticalTabLayout
      title={property.name}
      currentTab={tab}
      tabs={allowedTabs}
      getTabRoute={getTabRoute}
      isLoading={isLoading}
      onNavigate={onNavigate}
    >
      <Outlet
        context={
          {
            entity: property,
            setEntity: setProperty,
            formMode,
            setFormMode,
            displayMode: 'property',
          } satisfies PanelRootContext<Property>
        }
      />
    </VerticalTabLayout>
  );
};

export const propertiesAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<PropertiesActionHandler>> => {
  const { body, handler } = await request.json();
  const { tenantID, propertyID, permitID } =
    params as PropertyRouteParams['params'];
  let res: Response;

  try {
    switch (handler) {
      case PropertiesActionHandler.CREATE:
        res = await createProperty({ body, tenantID });
        break;
      case PropertiesActionHandler.UPDATE:
        res = await updateProperty({ propertyID, tenantID, body });
        break;
      case PropertiesActionHandler.UPDATE_CONTACTS:
        res = await putPropertyContacts({ tenantID, propertyID, body });
        break;
      case PropertiesActionHandler.UPDATE_USERS:
        res = await putPropertyUsers({ tenantID, propertyID, body });
        break;
      case PropertiesActionHandler.UPDATE_RULES:
        res = await putPropertyRules({ tenantID, propertyID, body });
        break;
      case PropertiesActionHandler.UPDATE_RULESETS:
        res = await putPropertyRulesets({ tenantID, propertyID, body });
        break;
      case PropertiesActionHandler.UPDATE_PERMITS:
        res = await updatePermit({
          tenantID,
          permitID,
          entityType: EntityTypes.Property,
          entityID: propertyID,
          body,
        });
        break;
      case PropertiesActionHandler.UPDATE_QR_CODES:
        res = new Response('', {
          status: 211,
          statusText: 'NEED TO IMPLEMENT UPSERT QR CODES',
        });
        break;
      default:
        res = new Response('Unknown handler', {
          status: 404,
          statusText: 'Unknown Handler',
        });
    }
  } catch (error) {
    res = new Response('', { status: 500, statusText: 'Network Error' });
  }

  return {
    handler,
    status: res.status,
    statusText: res.statusText,
  };
};
